import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import GatewayService from "../services/GatewayService";
import {
  GrantCustomerApplicationsInput,
  RemoveCustomerApplicationsInput,
  UpdateCustomerApplicationUrlInput,
} from "../components/supportFunctions/graphql/ApplicationsQueries";

export interface CustomerApplicationData {
  id: string;
  applicationUrl?: string;
}

export enum CustomerApplicationActions {
  EDIT = "edit",
  ASSIGN = "assign",
  UNASSIGN = "unassign",
}

const useCustomerApplicationMutation = () => {
  const edit = useMutation(
    (req: UpdateCustomerApplicationUrlInput) =>
      GatewayService.editCustomerApplication(req),
    ResponseHandling("applications_updated_successfully")
  );
  const assign = useMutation(
    (req: GrantCustomerApplicationsInput) =>
      GatewayService.assignCustomerApplication(req),
    ResponseHandling("applications_assigned_successfully")
  );
  const unassign = useMutation(
    (req: RemoveCustomerApplicationsInput) =>
      GatewayService.unassignCustomerApplication(req),
    ResponseHandling("applications_removed_successfully")
  );

  const mutate = async (
    action: CustomerApplicationActions,
    input: CustomerApplicationData,
    customerId: string
  ) => {
    switch (action) {
      case CustomerApplicationActions.EDIT:
        const editApp = mapToEdit(input, customerId);
        return await edit.mutateAsync(editApp);
      case CustomerApplicationActions.ASSIGN:
        const assignApp = mapToAssign(input, customerId);
        return await assign.mutateAsync(assignApp);
      case CustomerApplicationActions.UNASSIGN:
        const unassignApp = mapToUnassign(input, customerId);
        return await unassign.mutateAsync(unassignApp);
      default:
        throw new Error(`Invalid action: ${action}`);
    }
  };

  return {
    mutations: {
      mutate,
      isLoading: {
        any: edit.isLoading || assign.isLoading || unassign.isLoading,
        edit: edit.isLoading,
        assign: assign.isLoading,
        unassign: unassign.isLoading,
      },
    },
  };
};

export default useCustomerApplicationMutation;

function mapToEdit(
  input: CustomerApplicationData,
  customerId: string
): UpdateCustomerApplicationUrlInput {
  return {
    customerId,
    applicationId: input.id,
    applicationUrl: input.applicationUrl,
  };
}

function mapToAssign(
  input: CustomerApplicationData,
  customerId: string
): GrantCustomerApplicationsInput {
  return {
    customerId,
    applicationIds: [input.id],
    applications: [{ id: input.id, url: input.applicationUrl }],
  };
}

function mapToUnassign(
  input: CustomerApplicationData,
  customerId: string
): RemoveCustomerApplicationsInput {
  return {
    customerId,
    applicationIds: [input.id],
  };
}

function ResponseHandling(messageKey: string) {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    onSuccess: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
    },
  };
}
